/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import * as moment from "moment";
import { environment } from 'src/environments/environment';
import { TransactionType } from 'src/app/model/bvq/enums/Transaction.type';
import { ApiHeaders } from '../headers';
import { DownloadType } from 'src/app/model/bvq/enums/donwload.type';
import { IndustryRecord } from 'src/app/model/bvq/industry';
import { TransactionFilter } from 'src/app/model/bvq/transaction-filters';

@Injectable()
export class LookupProvider {

  constructor(private http: HttpClient) {}

  search(query?: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/naics?query=${query}`);
  }

  /** Get Industry Lookup by param
   * bc is for BizComps
   * ds is for DealStats
   * */
  industryTransaction(param: TransactionType, page: number, code: string, query: string = '',
    revenueMin: number = null, revenueMax: number = null,
    sdeMin: number = null, sdeMax: number = null,
    priceMin: number = null, priceMax: number = null,
    priceRevenueMin: number = null, priceRevenueMax: number = null,
    priceSdeMin: number = null, priceSdeMax: number = null,
    saleDateMin: string = '', saleDateMax: string = '', pageSize: number = null,
    include: string[] = [], exclude: string[] = [], selectedIds: string[] = [], 
    sortBy: string = '', sortDirection: string = '', tranType: string = ''
  ): Observable<any> {
    let type;
    switch (param) {
    case TransactionType.BIZCOMP:
      type = 'bc';
      break;
    case TransactionType.DEALSTATS:
      type = 'ds';
      break;
    default:
      type = 'ds';
      break;
    }
    return this.http.get<any>(environment.API_URL + '/api/industry/' + type + '/lookup?page=' + page +
      (code ? '&code=' + code : '')+
      (query ? '&query=' + query : '') +
      (revenueMin != null ? '&revenueMin=' + revenueMin : '') +
      (revenueMax != null ? '&revenueMax=' + revenueMax : '') +
      (sdeMin != null ? '&sdeMin=' + sdeMin : '') +
      (sdeMax != null ? '&sdeMax=' + sdeMax : '') +
      (priceMin != null ? '&priceEbitdaMin=' + priceMin : '') +
      (priceMax != null ? '&priceEbitdaMax=' + priceMax : '') +
      (priceRevenueMin != null ? '&priceRevenueMin=' + priceRevenueMin : '') +
      (priceRevenueMax != null ? '&priceRevenueMax=' + priceRevenueMax : '') +
      (priceSdeMin != null ? '&priceSdeMin=' + priceSdeMin : '') +
      (priceSdeMax != null ? '&priceSdeMax=' + priceSdeMax : '') +
      (saleDateMin ? '&saleDateMin=' + saleDateMin : '') +
      (saleDateMax ?'&saleDateMax=' + saleDateMax : '') +
      (pageSize ? '&pageSize=' + pageSize : '') +
      (include ? '&include=' + include : '') +
      (exclude ? '&exclude=' + exclude : '') +
      (selectedIds? '&selectedIds=' + selectedIds : '') + 
      (sortBy ? '&sortBy=' + sortBy : '' ) +
      (sortBy ? '&sortType=' + sortDirection : '' ) +
      (tranType ? '&tranType=' + tranType : '' )
    );
  }

  /** Get Industry Summary by param */
  industrySummary(param: TransactionType, page: number, code: string, query: string = '',
                  revenueMin: number = null, revenueMax: number = null,
                  sdeMin: number = null, sdeMax: number = null,
                  priceMin: number = null, priceMax: number = null,
                  priceRevenueMin: number = null, priceRevenueMax: number = null,
                  priceSdeMin: number = null, priceSdeMax: number = null,
                  saleDateMin: string = '', saleDateMax: string = '', pageSize: number = null,
                  include: string[] = [], exclude: string[] = [], selectedIds: string[] = [],
                  percentile: number = null, hidden: string[] = [], tranType: string = ''): Observable<any> {

    let type;
    switch (param) {
    case TransactionType.BIZCOMP:
      type = 'bc';
      break;
    case TransactionType.DEALSTATS:
      type = 'ds';
      break;
    default:
      type = 'ds';
      break;
    }
    return this.http.get<any>(environment.API_URL +  '/api/industry/' + type + '/summary?page=' + page +
      (code ? '&code=' + code : '') +
      (query ? '&query=' + query : '') +
      (revenueMin != null ? '&revenueMin=' + revenueMin : '') +
      (revenueMax != null ? '&revenueMax=' + revenueMax : '') +
      (sdeMin != null ? '&sdeMin=' + sdeMin : '') +
      (sdeMax != null ? '&sdeMax=' + sdeMax : '') +
      (priceMin != null ? '&priceEbitdaMin=' + priceMin : '') +
      (priceMax != null ? '&priceEbitdaMax=' + priceMax : '') +
      (priceRevenueMin != null ? '&priceRevenueMin=' + priceRevenueMin : '') +
      (priceRevenueMax != null ? '&priceRevenueMax=' + priceRevenueMax : '') +
      (priceSdeMin != null ? '&priceSdeMin=' + priceSdeMin : '') +
      (priceSdeMax != null ? '&priceSdeMax=' + priceSdeMax : '') +
      (saleDateMin ? '&saleDateMin=' + saleDateMin : '') +
      (saleDateMax ?'&saleDateMax=' + saleDateMax : '') +
      (include ? '&include=' + include : '') +
      (exclude ? '&exclude=' + exclude : '') +
      (selectedIds ? '&selectedIds=' + selectedIds : '') +
      (hidden ? '&hidden=' + hidden : '') +
      (percentile ? '&percentile=' + percentile : '') +
      (tranType ? '&tranType=' + tranType : ''));
  }

  removeBizComp(id: string): Observable<any> {
    return this.http.delete<any>(environment.API_URL +  '/' + id);
  }

  downLoadTransaction(param: TransactionType, codes: string, query: string = '',
    revenueMin: number = null, revenueMax: number = null,
    sdeMin: number = null, sdeMax: number = null,
    priceMin: number = null, priceMax: number = null,
    priceRevenueMin: number = null, priceRevenueMax: number = null,
    priceSdeMin: number = null, priceSdeMax: number = null,
    saleDateMin: string = '', saleDateMax: string = '', pageSize: number = null,
    include: string[] = [], exclude: string[] = [], sortBy: string = '', sortDirection: string = '',
    selectedBc: string = '', selectedDs: string = '', hidden: string[] = [], percentile: number, tranType: string = ''): Observable<any> {
    let type;
    switch (param) {
    case TransactionType.BIZCOMP:
      type = 'bc';
      break;
    case TransactionType.DEALSTATS:
      type = 'ds';
      break;
    default:
      type = 'ds';
      break;
    }
    return this.http
      .get(environment.API_URL +  '/api/industry/export/' + type + '/excel?codes=' + codes +
        (query ? '&query=' + query : '') +
        (revenueMin != null ? '&revenueMin=' + revenueMin : '') +
        (revenueMax != null ? '&revenueMax=' + revenueMax : '') +
        (sdeMin != null ? '&sdeMin=' + sdeMin : '') +
        (sdeMax != null ? '&sdeMax=' + sdeMax : '') +
        (priceMin != null ? '&priceEbitdaMin=' + priceMin : '') +
        (priceMax != null ? '&priceEbitdaMax=' + priceMax : '') +
        (priceRevenueMin != null ? '&priceRevenueMin=' + priceRevenueMin : '') +
        (priceRevenueMax != null ? '&priceRevenueMax=' + priceRevenueMax : '') +
        (priceSdeMin != null ? '&priceSdeMin=' + priceSdeMin : '') +
        (priceSdeMax != null ? '&priceSdeMax=' + priceSdeMax : '') +
        (saleDateMin ? '&saleDateMin=' + saleDateMin : '') +
        (saleDateMax ?'&saleDateMax=' + saleDateMax : '') +
        (pageSize ? '&pageSize=' + pageSize : '') +
        (include ? '&include=' + include : '') +
        (exclude ? '&exclude=' + exclude : '') +
        (sortBy ? '&sortBy=' + sortBy : '' ) +
        (sortBy ? '&sortType=' + sortDirection : '' ) +
        (selectedBc ? '&selectedBc=' + selectedBc : '') +
        (selectedDs ? '&selectedDs=' + selectedDs : '') +
        (percentile ? '&percentile=' + percentile : '') +
        (tranType ? '&tranType=' + tranType : '') +
        (hidden ? '&hidden=' + hidden : ''), ApiHeaders.getOptionsPlain());
  }

  downLoadIndustryZip(codes: string, downloadtype: DownloadType, selectedBc: any, selectedDs: any, bcFilter: any, dsFilter: any): Observable<any> {
    let type;
    switch (downloadtype) {
    case DownloadType.Initial:
      type = 'initial';
      break;
    case DownloadType.Financial:
      type = 'financial';
      break;
    case DownloadType.BizComp:
      type = 'bizcomp';
      break;
    case DownloadType.DealStats:
      type = 'dealstats';
      break;
    default:
      type = 'initial';
      break;
    }


    if (bcFilter && bcFilter.SALE_DATE_MIN)
      bcFilter.SALE_DATE_MIN = moment(bcFilter.SALE_DATE_MIN).format('MM/DD/YYYY');
    if (bcFilter && bcFilter.SALE_DATE_MAX)
      bcFilter.SALE_DATE_MAX = moment(bcFilter.SALE_DATE_MAX).format('MM/DD/YYYY');

    if (dsFilter && dsFilter.SALE_DATE_MIN)
      dsFilter.SALE_DATE_MIN = moment(dsFilter.SALE_DATE_MIN).format('MM/DD/YYYY');
    if (dsFilter && dsFilter.SALE_DATE_MAX)
      dsFilter.SALE_DATE_MAX = moment(dsFilter.SALE_DATE_MAX).format('MM/DD/YYYY');

    let body = {
      codes: codes,
      selectedBc: selectedBc,
      selectedDs: selectedDs,
      bcFilter,
      dsFilter
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    return this.http
      .post<any>(environment.API_URL +  '/file/export/' + type, body, {headers: headers, responseType: 'blob' as 'json' });
   }

  getIndustryRecords(page: number, query: string): Observable<any> {
    return this.http.get<any>(environment.API_URL +  '/records?page=' + page +
      (query ? '&query=' + query : ''));
  }

  getIndustryRecord(id: string): Observable<any> {
    return this.http.get<any>(environment.API_URL + '/records/' + id);
  }

  deleteIndustryRecord(id: string): Observable<any> {
    return this.http.delete<any>(environment.API_URL + '/records/' + id);
  }

  saveIndustryRecord(record: IndustryRecord): Observable<any> {
    if (record.FILTERS && record.FILTERS.length > 0) {
      let filter = record.FILTERS.find(r => r.TYPE == TransactionType.DEALSTATS);
      if (filter && filter.TRAN_TYPE == '')
        delete filter['TRAN_TYPE'];
    }
    return this.http.post<any>(environment.API_URL + '/records', record);
  }

  updateIndustryRecord(record: IndustryRecord): Observable<any> {
    if (record.FILTERS && record.FILTERS.length > 0) {
      let filter = record.FILTERS.find(r => r.TYPE == TransactionType.DEALSTATS);
      if (filter && filter.TRAN_TYPE == '')
        delete filter['TRAN_TYPE'];
    }
    return this.http.put<any>(environment.API_URL + '/records', record);
  }

  loadRMA(code: string): Observable<any> {
    return this.http
      .get(environment.API_URL + '/rma/' + code);
  }

  getIndustryDashboardTrans(page: number, code: string): Observable<any> {
    return this.http.get<any>(environment.API_URL + '/records?page=' + page + '&code=' + code);
  }

  downLoadFile(id: string): Observable<any> {
    return this.http
      .get(environment.API_URL + '/file/download/' + id, ApiHeaders.getOptionsPlain());
  }

  downloadZipByCode(code: string, type: string): Observable<any> {
    return this.http
      .get(environment.API_URL + '/file/' + type +'/zip/' + code, ApiHeaders.getOptionsPlain());
  }

  getNaicsSicList(desc: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/find?query=${desc}`);
  }

  // Filter Dashboard API

  getFilterRecords(type: TransactionType, page: number, query: string,code?: string): Observable<any> {
    return this.http.get<any>(environment.API_URL + 'api/industry/filter/all/' + type + '?page=' + page +
      (query ? '&query=' + query : '')+(code ?  '&code=' + code :  ''));
  }

  getFilterRecord(id: string): Observable<any> {
    return this.http.get<any>(environment.API_URL + '/api/industry/filter/' + id);
  }

  deleteFilterRecord(id: string): Observable<any> {
    return this.http.delete<any>(environment.API_URL + '/api/industry/filter/' + id);
  }

  saveFilterRecord(record: TransactionFilter): Observable<any> {
    if (record.TRAN_TYPE == '')
      delete record['TRAN_TYPE'];
    return this.http.post<any>(environment.API_URL + '/api/industry/filter', record);
  }

  updateFilterRecord(record: TransactionFilter): Observable<any> {
    if (record.TRAN_TYPE == '')
      delete record['TRAN_TYPE'];
    return this.http.put<any>(environment.API_URL + '/api/industry/filter', record);
  }

  getBcExcluded(excluded: any): Observable<any> {
    return this.http.get<any>(environment.API_URL + `/api/industry/bc/lookup/excluded?ids=${excluded}`);
  }

  getDsExcluded(excluded: any): Observable<any> {
    return this.http.get<any>(environment.API_URL + `/api/industry/ds/lookup/excluded?ids=${excluded}`);
  }

  updateFilterField(id: string, field: string, value: any): Observable<any> {
    return this.http.put<any>(environment.API_URL + `/api/industry/filter/${id}/field`, value, ApiHeaders.getOptions());
  }

  mapFilterWithBvq(reportId:string ,type: TransactionType,id: string, value: any){
    return this.http.put<any>(environment.API_URL + `/api/industry/filter/report/${reportId}/${type}/${id}`, value);
  }

  saveFilterWithBvq(id: string, value: any){
    return this.http.put<any>(environment.API_URL + `/api/industry/filter/${id}/update`, value);
  }
}
